import { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Group, Select, Stack, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { ACCESS_RULE_LABELS, type AccessRule, type ContentOption } from '@creativitas/shared';

/**
 * Кому доступен материал (§10, §18.6).
 *
 * Администратор отмечает основания доступа; достаточно любого одного. Движка
 * правил здесь нет — только набор, который сохраняется на сервере.
 *
 * «Доступ выдан администратором» среди галочек больше нет: точечная выдача из
 * карточки пользователя открывает материал при любых отмеченных правилах, и
 * галочка ничего не меняла. В старых настройках она могла остаться — редактор
 * её не показывает и при сохранении не возвращает.
 *
 * «Всем зарегистрированным» перекрывает остальные основания, поэтому, пока
 * оно отмечено, остальные сняты и недоступны.
 */

const EDITOR_RULES = ['free', 'trial', 'light', 'pro', 'game_owner'] as const satisfies readonly AccessRule[];

/** То, что редактор показывает и сохраняет: без `manual`, а при `free` — только `free`. */
function editable(rules: readonly AccessRule[]): AccessRule[] {
  if (rules.includes('free')) return ['free'];
  return EDITOR_RULES.filter((rule) => rules.includes(rule));
}

type AccessRulesEditorProps = {
  value: AccessRule[];
  requiredGameId: string | null;
  games: ContentOption[];
  saving?: boolean;
  onSave: (rules: AccessRule[], requiredGameId: string | null) => void;
};

export function AccessRulesEditor({ value, requiredGameId, games, saving, onSave }: AccessRulesEditorProps) {
  const baseline = editable(value);
  const [rules, setRules] = useState<AccessRule[]>(baseline);
  const [gameId, setGameId] = useState<string | null>(requiredGameId);

  /**
   * Re-sync only when the server's answer actually changed. A refetch hands us
   * a new array with the same contents on every poll — keying the effect on
   * identity would silently discard edits the admin is in the middle of making.
   */
  const serverValue = baseline.join(',');
  useEffect(() => {
    setRules(serverValue ? (serverValue.split(',') as AccessRule[]) : []);
  }, [serverValue]);

  useEffect(() => setGameId(requiredGameId), [requiredGameId]);

  const everyone = rules.includes('free');
  const needsGame = rules.includes('game_owner');
  const dirty =
    rules.length !== baseline.length ||
    rules.some((rule) => !baseline.includes(rule)) ||
    (needsGame && gameId !== requiredGameId);
  const canSave = dirty && (!needsGame || Boolean(gameId));

  const gameTitle = games.find((game) => game.id === gameId)?.title;
  const labelOf = (rule: AccessRule) =>
    rule === 'game_owner' && gameTitle ? `${ACCESS_RULE_LABELS.game_owner} «${gameTitle}»` : ACCESS_RULE_LABELS[rule];

  const toggle = (rule: AccessRule, checked: boolean) => {
    if (rule === 'free') {
      setRules(checked ? ['free'] : []);
      return;
    }
    /* Игра одна — выбирать её не из чего: подставляем сразу, и в подписи видно, какая. */
    if (rule === 'game_owner' && checked && !gameId && games.length === 1) setGameId(games[0]!.id);
    setRules((current) => (checked ? [...current, rule] : current.filter((item) => item !== rule)));
  };

  return (
    <Stack gap="sm">
      <Stack gap={6}>
        {EDITOR_RULES.map((rule) => (
          <Checkbox
            key={rule}
            checked={rules.includes(rule)}
            disabled={everyone && rule !== 'free'}
            label={labelOf(rule)}
            onChange={(event) => toggle(rule, event.currentTarget.checked)}
          />
        ))}
        {/* Про включает Лайт (docs/PAYMENTS.md, §4.1): галочку Про рядом с Лайт ставить незачем. */}
        <Text size="xs" c="var(--text-subtle)" data-testid="access-pro-includes-light">
          Тариф «Про» включает «Лайт»: отмеченное «По тарифу „Лайт“» открыто и подписчикам «Про».
        </Text>
      </Stack>

      {needsGame ? (
        <Select
          label="Какую игру нужно иметь"
          placeholder="Выберите игру"
          data={games.map((game) => ({ value: game.id, label: game.title }))}
          value={gameId}
          onChange={setGameId}
          error={!gameId ? 'Выберите игру' : undefined}
        />
      ) : null}

      {rules.length === 0 ? (
        <Alert variant="light" color="gold" icon={<IconInfoCircle size={16} />} p="sm">
          <Text size="xs">
            Ничего не выбрано — материал закрыт для пользователей. Открыть его конкретному человеку можно в
            карточке пользователя.
          </Text>
        </Alert>
      ) : null}

      <Group>
        <Button size="sm" disabled={!canSave} loading={saving} onClick={() => onSave(rules, needsGame ? gameId : null)}>
          Сохранить доступ
        </Button>
        {dirty ? (
          <Button
            size="sm"
            variant="subtle"
            onClick={() => {
              setRules(baseline);
              setGameId(requiredGameId);
            }}
          >
            Отменить
          </Button>
        ) : null}
      </Group>
    </Stack>
  );
}
